/**
 * EstimateCheck — public lead-capture form at `/estimate`. Customer
 * uploads a photo of a garage's repair estimate, provides basic contact
 * info, and one of our experts follows up manually. MVP: no payment,
 * no in-app verdict — pure lead capture.
 */
import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Upload, CheckCircle2, Loader2, FileText, ShieldCheck, Clock, Camera, X } from 'lucide-react';
import PropitstopNav from '../components/PropitstopNav';
import PropitstopFooter from '../components/PropitstopFooter';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_PHOTO_MB = 8;

export default function EstimateCheck() {
  const [form, setForm] = useState({
    name: '', mobile: '', city: 'Bengaluru',
    car_make_model: '', garage_name: '', estimate_amount: '', notes: '',
  });
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_PHOTO_MB * 1024 * 1024) {
      setError(`Photo is larger than ${MAX_PHOTO_MB} MB. Try compressing it first.`);
      return;
    }
    setError('');
    setPhoto(f);
    setPreview(URL.createObjectURL(f));
  };

  const clearPhoto = () => {
    setPhoto(null);
    setPreview('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      if (photo) fd.append('photo', photo);
      await axios.post(`${API}/api/public/estimate-requests`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-white text-zinc-900" data-testid="estimate-thankyou">
        <PropitstopNav />
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-50 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="mt-6 text-3xl font-extrabold tracking-tight">Estimate received — our expert is on it.</h1>
          <p className="mt-3 text-zinc-500">
            Thank you {form.name.split(' ')[0]}. One of our automotive experts will call you on <b>{form.mobile}</b> shortly to walk you through whether the quote is fair.
          </p>
          <div className="mt-8 grid sm:grid-cols-3 gap-4 text-left">
            <InfoCard icon={Clock} title="Response within 4 hours" desc="During business hours (10 AM–7 PM IST)." />
            <InfoCard icon={ShieldCheck} title="Independent opinion" desc="We're not tied to the garage that quoted you." />
            <InfoCard icon={FileText} title="Verdict on WhatsApp" desc="You'll also get a written summary." />
          </div>
        </div>
        <PropitstopFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="estimate-page">
      <PropitstopNav />

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16 grid lg:grid-cols-[1fr,1.2fr] gap-8 lg:gap-12">
        {/* Left rail — pitch */}
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-orange-600 font-bold">Expert Opinion</div>
          <h1 className="mt-2 text-3xl lg:text-4xl font-extrabold tracking-tight text-zinc-900 leading-tight">
            Is your repair estimate <span className="text-orange-600">fair</span>?
          </h1>
          <p className="mt-4 text-zinc-500 leading-relaxed">
            Upload a photo of the estimate you received from any garage. An automotive expert will call you back within a few hours with an independent opinion — regardless of which workshop your car is in.
          </p>
          <div className="mt-8 space-y-4">
            <Row icon={ShieldCheck} title="Independent & unbiased" desc="We don't own the garage that quoted you." />
            <Row icon={Clock} title="Fast turnaround" desc="Most estimates reviewed within 4 hours." />
            <Row icon={FileText} title="Written verdict" desc="You get a WhatsApp summary you can share back." />
          </div>
        </div>

        {/* Right rail — form */}
        <form onSubmit={submit} className="bg-zinc-50 rounded-3xl p-6 lg:p-8 border border-zinc-200 space-y-4" data-testid="estimate-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Your name *" testid="est-name" value={form.name} onChange={(v) => setField('name', v)} placeholder="Rakesh Kumar" required />
            <Field label="Mobile *" testid="est-mobile" value={form.mobile} onChange={(v) => setField('mobile', v.replace(/[^\d+]/g, ''))} placeholder="+91 98xxx xxxxx" required type="tel" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="City *" testid="est-city" value={form.city} onChange={(v) => setField('city', v)} required />
            <Field label="Car (make + model)" testid="est-car" value={form.car_make_model} onChange={(v) => setField('car_make_model', v)} placeholder="Hyundai Creta 2020" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Garage that quoted" testid="est-garage" value={form.garage_name} onChange={(v) => setField('garage_name', v)} placeholder="ABC Auto Works" />
            <Field label="Estimate amount (₹)" testid="est-amount" value={form.estimate_amount} onChange={(v) => setField('estimate_amount', v.replace(/[^\d.]/g, ''))} placeholder="18450" inputMode="numeric" />
          </div>
          <Field label="Notes" testid="est-notes" value={form.notes} onChange={(v) => setField('notes', v)} placeholder="What went wrong, symptoms, anything else…" multiline />

          {/* Photo upload */}
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-600 font-bold block mb-1.5">Photo of the estimate</label>
            {preview ? (
              <div className="relative rounded-xl overflow-hidden border border-zinc-200">
                <img src={preview} alt="preview" className="w-full max-h-72 object-contain bg-zinc-50" />
                <button
                  type="button"
                  onClick={clearPhoto}
                  className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 text-white hover:bg-black/80"
                  data-testid="est-remove-photo"
                  aria-label="Remove photo"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label
                className="flex items-center justify-center gap-2 py-6 rounded-xl border-2 border-dashed border-zinc-300 hover:border-orange-400 hover:bg-orange-50/40 cursor-pointer transition-colors"
                data-testid="est-photo-dropzone"
              >
                <Camera className="w-5 h-5 text-zinc-500" />
                <span className="text-sm text-zinc-500">Tap to upload a photo or PDF screenshot</span>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={onFile}
                  className="hidden"
                  data-testid="est-photo-input"
                />
              </label>
            )}
            <p className="mt-1 text-[11px] text-zinc-400">JPG / PNG · up to {MAX_PHOTO_MB} MB. Not required, but massively speeds up our review.</p>
          </div>

          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="est-error">{error}</div>}

          <button
            type="submit"
            disabled={submitting}
            data-testid="est-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold disabled:opacity-50 transition-colors"
          >
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Upload className="w-4 h-4" /> Send for expert review</>}
          </button>
          <p className="text-[11px] text-zinc-400 text-center">By submitting you agree to be contacted by our team on the number above.</p>
        </form>
      </section>

      <PropitstopFooter />
    </div>
  );
}

function Field({ label, testid, value, onChange, placeholder, required, type = 'text', inputMode, multiline }) {
  const cls = 'w-full px-3 py-2.5 rounded-xl bg-white border border-zinc-200 focus:border-orange-400 outline-none text-sm placeholder-zinc-400';
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-zinc-600 font-bold block mb-1.5">{label}</label>
      {multiline ? (
        <textarea data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={3} className={cls} />
      ) : (
        <input data-testid={testid} type={type} inputMode={inputMode} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required={required} className={cls} />
      )}
    </div>
  );
}

function Row({ icon: Icon, title, desc }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-full bg-orange-50 flex items-center justify-center flex-shrink-0"><Icon className="w-4 h-4 text-orange-600" /></div>
      <div>
        <div className="text-sm font-bold text-zinc-900">{title}</div>
        <div className="text-xs text-zinc-500 mt-0.5">{desc}</div>
      </div>
    </div>
  );
}

function InfoCard({ icon: Icon, title, desc }) {
  return (
    <div className="bg-zinc-50 rounded-2xl p-4 border border-zinc-200">
      <Icon className="w-5 h-5 text-orange-600" />
      <div className="mt-2 text-sm font-bold text-zinc-900">{title}</div>
      <div className="text-xs text-zinc-500 mt-1">{desc}</div>
    </div>
  );
}
